"use client"
import { isMobile as isMobileDevice } from "react-device-detect";
import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../../context";
import {
  FaVideo,
  FaMicrophone,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { MdChat } from "react-icons/md";
import { useForm } from "react-hook-form";
import interviewApi, { InterviewTags, useReInterviewMutation, useSetUpInterviewMutation } from "../../redux/api/interview.api";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import {
  ConversationMode,
  InitializeInterviewRequest,
  Interviewer,
} from "../../types/interview.type";
import { toast } from "react-toastify";
import { useModal } from "../../contexts/modal.context";
import { LocalStorage } from "../../types/local_storage.enum";
import { SessionStorage } from "../../types/general.type";
import InterviewModeToggle from "./interview-mode-toggle";
import JobDescriptionField from "./job-description-field";
import { AnimatePresence, motion } from "framer-motion";
import MicPermissionHandler from "../../contexts/MicAccessDialog";
import PositionList from "../../pages/positions-list";
import { useScreen } from "../../contexts/screen.provider";
import { useAppDispatch } from "../../redux/hooks";

const interviewers: Interviewer[] = [
  {
    id: 1,
    name: "Andrew",
    voiceName: "en-US-AndrewMultilingualNeural",
    image: "/images/andrew2.png",
    faceId: process.env.REACT_APP_ANDREW_FACE_ID || "",
  },
  {
    id: 3,
    name: "Briyan",
    voiceName: "en-US-BrianMultilingualNeural",
    image: "/images/brian2.png",
    faceId: process.env.REACT_APP_BRIAN_FACE_ID || "",
  },
  {
    id: 5,
    name: "Ava",
    voiceName: "en-US-AvaMultilingualNeural",
    image: "/images/ava2.png",
    faceId: process.env.REACT_APP_AVA_FACE_ID || "",
  },
  {
    id: 6,
    name: "Emma",
    voiceName: "en-US-EmmaMultilingualNeural",
    image: "/images/emma2.png",
    faceId: process.env.REACT_APP_EMMA_FACE_ID || "",
  },
];

const durations: number[] = [5, 10, 15, 20];

const InterviewSetup: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [noJobFound, setNoJobsFound] = useState(false);
  const isNew = searchParams.get("newPosition") === "true";
  const jobId = +searchParams.get("jobId")!;
  const interviewInitialisedRef = useRef<boolean>(false)
  const [jobSelectionError, setJobSelectionError] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(jobId || null);
  const [newPosition, setNewPosition] = useState(false);
  const { theme } = useTheme();
  const [tryToSubmit, setTryToSubmit] = useState(false);
  const [micAllowed, setMicAllowed] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const isSpeechRecognitionSupported = (!!window.SpeechRecognition || !!window.webkitSpeechRecognition) && !((navigator as any).brave?.isBrave() && !isMobileDevice);
  const { openModal, closeModal } = useModal();
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedDuration, setSelectedDuration] = useState<number>(5);
  const [conversationMode, setConversationMode] = useState<ConversationMode>(
    isSpeechRecognitionSupported ? ConversationMode.VIDEO : ConversationMode.CHAT
  );
  const [isAuto, setIsAuto] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isMobile } = useScreen()
  const [isAudioMuted, setIsAudioMuted] = useState<boolean | null>(null);

  useEffect(() => {
    if (isNew) {
      setNewPosition(true);
    }
  }, [isNew]);

  useEffect(() => {
    setConversationMode(!isSpeechRecognitionSupported ? ConversationMode.CHAT : (isMobile ? ConversationMode.VOICE : ConversationMode.VIDEO));
  }, [isMobile, isSpeechRecognitionSupported])
  // Check if audio output is muted by attempting to play a silent audio
  const checkAudioOutput = async () => {
    try {
      const audio = new Audio();
      audio.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=";
      audio.play().then(() => {
        setIsAudioMuted(false);
      }).catch(err => {
        setIsAudioMuted(true);
      });
    } catch (err) {
      setIsAudioMuted(true);

    }
  };



  useEffect(() => {

    checkAudioOutput();
  }, []);

  useEffect(() => {
    // Add custom scrollbar and input styles
    const style = document.createElement("style");
    style.textContent = `
      @keyframes border-pulse {
        0% { border-color: ${theme === "dark" ? "rgba(59,130,246,0.3)" : "rgba(59,130,246,0.2)"}; }
        50% { border-color: ${theme === "dark" ? "rgba(59,130,246,0.5)" : "rgba(59,130,246,0.4)"}; }
        100% { border-color: ${theme === "dark" ? "rgba(59,130,246,0.3)" : "rgba(59,130,246,0.2)"}; }
      }
      @keyframes focus-pulse {
        0% { border-color: rgba(8,191,177,0.8); }
        50% { border-color: rgba(8,191,177,1); }
        100% { border-color: rgba(8,191,177,0.8); }
      }
      .animate-border-pulse {
        animation: border-pulse 2s infinite;
      }
      .animate-focus-pulse {
        animation: focus-pulse 1.5s infinite;
      }
      .job-description-field textarea, .job-description-field input {
        background: ${theme === 'dark' ? '#2e2d32' : '#f5f5f5'};
        border: 1px solid rgba(59,130,246,0.3);
        border-radius: 6px;
        transition: border-color 0.2s;
        color: ${theme === "dark" ? "rgba(209,213,219,1)" : "rgba(55,65,81,1)"};
      }
      .job-description-field textarea:focus, .job-description-field input:focus {
        border-color: rgba(8,191,177,1);
        outline: none;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [theme]);

  useEffect(() => {
    if (isSubmitting) openModal("Preparing interview session, please wait…");
    else {
      closeModal();
    }
  }, [isSubmitting]);

  useEffect(() => {
    let timeOut: NodeJS.Timeout | null = null;
    if (error) {
      timeOut = setTimeout(() => {
        setError(null);
      }, 5000);
    }
    return () => {
      if (timeOut) clearTimeout(timeOut);
    };
  }, [error]);

  const withSound = conversationMode !== ConversationMode.CHAT;
  useEffect(() => {
    if (interviewInitialisedRef.current) return;
    const data = JSON.parse(localStorage.getItem('data') || '{}') as InitializeInterviewRequest | null
    if (((micAllowed && !isMuted && withSound) || !withSound) && (data?.JobDescription || selectedJobId || jobId)) {
      initializeInterview(data)
    }
  }, [tryToSubmit, micAllowed]);

  const nextSlide = (e: {
    preventDefault: () => void;
    stopPropagation: () => void;
  }) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedIndex((prev) => (prev + 1) % interviewers.length);
  };

  const prevSlide = (e: {
    preventDefault: () => void;
    stopPropagation: () => void;
  }) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedIndex(
      (prev) => (prev - 1 + interviewers.length) % interviewers.length
    );
  };

  const handleDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedDuration(parseInt(event.target.value, 10));
  };

  const handleModeChange = (mode: ConversationMode) => {
    setConversationMode(mode);
  };

  const getVisibleInterviewers = () => {
    const leftIndex =
      (selectedIndex - 1 + interviewers.length) % interviewers.length;
    const rightIndex = (selectedIndex + 1) % interviewers.length;
    return [
      interviewers[leftIndex],
      interviewers[selectedIndex],
      interviewers[rightIndex],
    ];
  };


  const [setUpInterview] = useSetUpInterviewMutation();
  const [reinterview] = useReInterviewMutation()
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<InitializeInterviewRequest>({
    defaultValues: {
      JobDescription: "",
      MaxQuestions: "5",
      BotName: "",
      Title: "",
    },
  });




  const handleSave = async (data: InitializeInterviewRequest) => {
    // if(isAudioMuted){
    //   toast.error("The site audio output devices are muted. Please check your audio settings.");
    //   return;
    // }
    if (!selectedJobId && !data.JobDescription) {
      setJobSelectionError(true);
      return;
    }
    if (selectedDuration < 5) return;
    setIsSubmitting(true);
    setTryToSubmit(true);
    localStorage.setItem('data', JSON.stringify({
      ...data,
      conversationMode
    }))
  };


  const initializeInterview = async (data: InitializeInterviewRequest | null) => {
    try {
      await (
        ((jobId && typeof jobId === 'number') || selectedJobId)
          ? reinterview({
            Duration: selectedDuration,
            BotName: interviewers[selectedIndex].name,
            JobId: selectedJobId || jobId,
          })
          : setUpInterview({
            ...data,
            BotName: interviewers[selectedIndex].name,
          })
      )
        .unwrap()
        .then((res) => {
          interviewInitialisedRef.current = true
          const setup = {
            conversationMode: conversationMode,
            isAuto: isAuto,
            started: false,
            paused: false,
            completed: false,
          };
          const interviewStateData = {
            sentData: data,
            selectedInterviweer: interviewers[selectedIndex],
            setup: setup,
            initialResponse: { ...res, botName: data?.BotName },
          };

          if (!((jobId && typeof jobId === 'number') || selectedJobId)) {
            localStorage.removeItem(LocalStorage.LOCAL_INTERVIEWD_LIST)
          }

          reset({
            JobDescription: "",
            MaxQuestions: "5",
            BotName: "",
            Title: "",
          });
          searchParams.delete("jobId")
          setSearchParams(searchParams);
          setSelectedJobId(null);
          localStorage.removeItem('data');
          dispatch(interviewApi.util.invalidateTags([InterviewTags.SEARCH_JOBS_FOR_INTERVIEW]))
          sessionStorage.setItem(SessionStorage.INTERVIEW_STATE, JSON.stringify(interviewStateData));
          window.location.href = '/current-interview'
          if (isAuto) localStorage.setItem(LocalStorage.IS_NEW, "true");
        })
        .catch((err: any) => {
          setError(err.data.errorMessage);
          if ("data" in err) {
            const {
              errors: { title },
            } = err.data as { errors: { title: string } };
            toast.error(title || "Can not initialize interview");
          } else {
            toast.error("Can not initialize for interview");
          }
        })
        .finally(() => {
          setIsSubmitting(false);
          setTryToSubmit(false);
          closeModal();
        });
    } catch (error) {
    }
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(handleSave)}
        className={` relative w-[95%] md:w-[96%] lg:w-[96%] tablet:self-start py-6 bg-white dark:bg-[#232226]   rounded-xl `}
      >
        <div className="flex justify-center pt-2 w-full items-center">
          <p
            className={`text-lg font-bold ${theme === "dark" ? "text-gray-100" : "text-gray-900"} text-center mb-4`}
          >
            Setup Your interview
          </p>
        </div>
        <div className="flex w-full pl-3 items-center relative">
          <AnimatePresence>
            {error && (
              <motion.div
                key="oos-warning"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className={`fixed top-10 right-5 z-[1200] w-[400px] px-6 py-4 rounded-xl flex items-start space-x-3 bg-red-400/90 border-l-4 border-red-500 shadow-[0_4px_6px_rgba(0,0,0,0.1),_0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_6px_rgba(0,0,0,0.3),_0_1px_3px_rgba(0,0,0,0.2)]`}
              >
                <AlertTriangle className="w-6 h-6 flex-shrink-0 text-white" />
                <div>
                  <p className="font-semibold text-white"></p>
                  <p className="text-sm text-white">{error}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setError(null)
                  }}
                  className="absolute right-3 top-2"
                >
                  <X className="text-white hover:text-gray-200" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className={`grid grid-cols-1 text-xs gap-[10px] sm:grid-cols-2 ${theme}`}>
          <div className={`flex flex-col p-4 !rounded-lg border border-accent-700/40  ${theme === "dark"
            ? "bg-[rgba(46,45,50,1)]"
            : "bg-[rgba(247,248,250,1)]"
            } shadow-[0_4px_6px_rgba(0,0,0,0.1),_0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_6px_rgba(0,0,0,0.3),_0_1px_3px_rgba(0,0,0,0.2)] job-description-field`}>
            {
              (newPosition && !jobId)
                ?
                <JobDescriptionField
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  watch={watch}
                  setNewPosition={setNewPosition}
                  noJobFound={noJobFound}
                />
                :
                <PositionList
                  setNoJobsFound={setNoJobsFound}
                  jobSelectionError={jobSelectionError}
                  setJobSelectionError={setJobSelectionError}
                  setNewPosition={setNewPosition}
                  selectedJobId={selectedJobId}
                  setSelectedJobId={setSelectedJobId}
                />
            }
          </div>

          <div className={`p-4 flex flex-col gap-4 !rounded-lg border border-accent-700/40  ${theme === "dark"
            ? "bg-[rgba(46,45,50,1)]"
            : "bg-[rgba(247,248,250,1)]"
            } shadow-[0_4px_6px_rgba(0,0,0,0.1),_0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_6px_rgba(0,0,0,0.3),_0_1px_3px_rgba(0,0,0,0.2)]`}>
            <h2 className={`text-xs font-semibold ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>
              Available Interviewers
            </h2>
            <div className="flex flex-col items-center">
              <div className="w-full flex items-center justify-center gap-4">
                <button
                  onClick={prevSlide}
                  className={`p-2 bg-accent-500 text-white hover:bg-accent-500/80 rounded-full transition duration-200 shadow-[0_2px_4px_rgba(0,0,0,0.1)]`}
                  aria-label="Previous interviewer"
                >
                  <FaChevronLeft size={12} />
                </button>
                <div className="w-[500px] overflow-hidden">
                  <div className="flex items-center justify-center gap-4">
                    {getVisibleInterviewers().map((interviewer, index) => {
                      const isCenter = index === 1;
                      return (
                        <div
                          key={interviewer.id}
                          className={`flex-shrink-0 ${isCenter ? "opacity-100" : "opacity-60"}`}
                        >
                          <img
                            src={interviewer.image}
                            alt={interviewer.name}
                            className={`rounded-full object-cover ${isCenter
                              ? "h-24 w-24 md:w-32 md:h-32 border-[1.5px] border-[#08bfb1] animate-focus-pulse"
                              : "h-12 w-12 md:w-16 md:h-16 border border-accent-700/40"
                              }`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
                <button
                  onClick={nextSlide}
                  className={`p-2 bg-accent-500 text-white hover:bg-accent-500/80 rounded-full transition duration-200 shadow-[0_2px_4px_rgba(0,0,0,0.1)]`}
                  aria-label="Next interviewer"
                >
                  <FaChevronRight size={12} />
                </button>
              </div>
              <p className={`text-center font-medium mt-2 ${theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
                {interviewers[selectedIndex].name}
              </p>
              {errors.BotName?.message && (
                <span className="text-red-500 text-xs">
                  {errors.BotName?.message}
                </span>
              )}
            </div>

            <div className="flex flex-col items-start">
              <label
                className={`block mb-2 text-xs text-slate-700 dark:text-slate-300 font-medium`}
              >
                Interview Duration
              </label>
              <div className="w-[100%] relative flex justify-center gap-2">
                <input
                  type="range"
                  min={durations[0]}
                  step={"5"}
                  {...register("MaxQuestions", {
                    required: "Interview duration is required",
                  })}
                  max={durations[durations.length - 1]}
                  value={selectedDuration}
                  onChange={handleDurationChange}
                  className={`w-full h-[5px] mb-1 appearance-none rounded-lg cursor-pointer focus:outline-none bg-transparent ${errors.MaxQuestions?.message && "border border-red-500"
                    } scrollbar-thumb-accent-500 scrollbar-track-transparent hover:scrollbar-thumb-accent-500/80`}
                  style={{
                    background: `linear-gradient(to right, #3fbba3 ${((selectedDuration - durations[0]) /
                      (durations[durations.length - 1] - durations[0])) * 100
                      }%, ${theme === "dark" ? "rgba(40,39,44,1)" : "rgba(200,200,200,1)"} ${((selectedDuration - durations[0]) /
                        (durations[durations.length - 1] - durations[0])) * 100
                      }%)`,
                  }}
                />
                <p
                  className={`flex gap-1 text-slate-700 dark:text-slate-300 font-medium min-w-[40px] text-xs`}
                >
                  {selectedDuration}
                  <span>min</span>
                </p>
              </div>
              {selectedDuration < 5 && (
                <span className="text-red-500 text-xs">
                  Interview duration can not be less than 5 min
                </span>
              )}
            </div>

            <div className="flex flex-col items-start">
              <label
                className={`block mb-2 text-xs font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}
              >
                Interview Mode
              </label>
              <div className="w-full text-[10px] md:text-xs grid grid-cols-3 gap-1 md:gap-2">
                {isSpeechRecognitionSupported && (
                  <>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleModeChange(ConversationMode.VIDEO);
                      }}
                      className={`flex-1 h-[30px] py-1 px-2 md:px-4 rounded-lg border border-accent-700/40 transition duration-200 shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${conversationMode === ConversationMode.VIDEO
                        ? "bg-accent-500 text-white"
                        : theme === "dark" ? "text-gray-100 bg-[rgba(46,45,50,1)]" : "text-gray-900 bg-[rgba(247,248,250,1)]"
                        } flex items-center  justify-center gap-1`}
                    >
                      <FaVideo
                        className={
                          conversationMode === ConversationMode.VIDEO
                            ? "text-white"
                            : "text-accent-500"
                        }
                      />
                      Video
                      <span className=" text-[8px] md:text-xs">(Beta)</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleModeChange(ConversationMode.VOICE);
                      }}
                      className={`flex-1 h-[30px] py-1 px-2 md:px-4 rounded-lg border border-accent-700/40 transition duration-200 shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${conversationMode === ConversationMode.VOICE
                        ? "bg-accent-500 text-white"
                        : theme === "dark" ? "text-gray-100 bg-[rgba(46,45,50,1)]" : "text-gray-900 bg-[rgba(247,248,250,1)]"
                        } flex items-center justify-center gap-2`}
                    >
                      <FaMicrophone
                        className={
                          conversationMode === ConversationMode.VOICE
                            ? "text-white"
                            : "text-accent-500"
                        }
                      />
                      Voice
                    </button>
                  </>
                )}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleModeChange(ConversationMode.CHAT);
                  }}
                  className={`flex-1 h-[30px] py-1 px-2 md:px-4 rounded-lg border border-accent-700/40 transition duration-200 shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${conversationMode === ConversationMode.CHAT
                    ? "bg-accent-500 text-white"
                    : theme === "dark" ? "text-gray-100 bg-[rgba(46,45,50,1)]" : "text-gray-900 bg-[rgba(247,248,250,1)]"
                    } flex items-center justify-center gap-2`}
                >
                  <MdChat
                    className={
                      conversationMode === ConversationMode.CHAT
                        ? "text-white"
                        : "text-accent-500"
                    }
                  />
                  Chat
                </button>
              </div>
            </div>
            {isSpeechRecognitionSupported && (
              <InterviewModeToggle
                isAuto={isAuto}
                setIsAuto={setIsAuto}
                theme={theme}
                disabled={conversationMode === ConversationMode.CHAT}
              />
            )}
          </div>
        </div>
        <div className="flex justify-center w-full items-center mt-2.5 mobile:mt-6 md:mt-10">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-[80%] md:w-[60%] text-sm py-2 px-8 rounded-lg border border-accent-700/40 transition duration-200 shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${theme === "dark"
              ? "bg-accent-500 text-white hover:bg-accent-500/80"
              : "bg-accent-500 text-white hover:bg-accent-500/80"
              } flex justify-center items-center disabled:opacity-80 disabled:cursor-not-allowed`}
          >
            Start Interview
          </button>
        </div>
      </form>
      {tryToSubmit && withSound && <MicPermissionHandler
        micAllowed={micAllowed}
        setMicAllowed={setMicAllowed}
        micMuted={isMuted}
        setMicMuted={setIsMuted}
        onCancel={() => {
          setIsSubmitting(false);
          setTryToSubmit(false);
          localStorage.removeItem('data');
          closeModal();
        }}
      />}
    </>
  );
};

export default InterviewSetup;